import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import {
  Typography,
  Box,
  IconButton,
  TextField,
  Breadcrumbs,
  Link as MUILink,
  CircularProgress,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import PageTitle from "../../Components/PageTitle";
import Table from "../common/Table";
import moment from "moment";
import AddEnrollStudentBatch from "./AddEnrollStudentBatch";
import {
  getBatchStudents,
  deleteStudent,
} from "../../redux/actions/admin/studentAction";
import EditAddEnrollStudentBatch from "./EditAddEnrollStudentBatch";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const EnrollStudentBatch = () => {
  const [value, setValue] = useState(0);
  const dispatch = useDispatch();
  const location = useLocation();
  const { students, loading } = useSelector((state) => state.studentData);
  const { batchId } = location.state || {};
  const [search, setSearch] = useState("");
  const [filteredStudent, setFilteredStudent] = useState([]);

  useEffect(() => {
    if (batchId) {
      dispatch(getBatchStudents(batchId));
    }
  }, [batchId, dispatch]);

  useEffect(() => {
    setFilteredStudent(students);
  }, [students]);

  // Function to refresh the admissions data
  const refreshData = () => {
    dispatch(getBatchStudents(batchId)); // Refresh data after update
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // Handle Search
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearch(value);
    const filteredData = students.filter(
      (item) =>
        item.name.toLowerCase().includes(value) ||
        item.email.toLowerCase().includes(value)
    );
    setFilteredStudent(filteredData);
  };

  // Delete Student
  const { confirm } = Modal;
  const handleDeleteStudent = (id) => {
    confirm({
      title: "Are you sure you want to delete this Student?",
      content: "Once deleted, this student cannot be recovered.",
      okText: "Yes, Delete",
      cancelText: "No, Cancel",
      onOk: async () => {
        await dispatch(deleteStudent(id));
        dispatch(getBatchStudents(batchId));
      },
    });
  };

  const columns = [
    {
      field: "rowNumber",
      headerName: "#",
      width: 10,
      sortable: false,
    },
    {
      field: "Actions",
      width: 120,
      renderCell: (value) => (
        <Box>
          <IconButton
            aria-label="delete"
            onClick={() => handleDeleteStudent(value?.row?._id)}
          >
            <DeleteIcon sx={{ color: "red" }} />
          </IconButton>
          <EditAddEnrollStudentBatch
            id={value?.row?._id}
            name={value?.row?.name}
            email={value?.row?.email}
            phone={value?.row?.phone}
            parentName={value?.row?.parentName}
            parentEmail={value?.row?.parentEmail}
            parentPhone={value?.row?.parentPhone}
            feesDeposit={value?.row?.feesDeposit}
            courseFees={value?.row?.courseFees}
            refreshData={refreshData}
          />
        </Box>
      ),
    },
    {
      field: "srNumber",
      headerName: "SR Number",
      width: 120,
    },
    {
      field: "name",
      headerName: "Student Name",
      width: 150,
    },

    {
      field: "email",
      headerName: "E-mail",
      width: 200,
      editable: true,
    },
    {
      field: "phone",
      headerName: "Phone Number",
      width: 150,
      editable: true,
    },
    {
      field: "feesDeposit",
      headerName: "Student Fees Deposit",
      width: 190,
      editable: true,
    },
    {
      field: "courseFees",
      headerName: "Class & Course Fees",
      width: 190,
      editable: true,
    },
    {
      field: "parentName",
      headerName: "Parent Name",
      width: 180,
      editable: true,
    },

    {
      field: "parentPhone",
      headerName: "Parent Phone",
      width: 180,
      editable: true,
    },

    {
      field: "createdAt",
      headerName: "Date",
      width: 150,
      renderCell: (params) =>
        moment(params?.row?.createdAt).format("MM/DD/YYYY"),
    },
  ];

  return (
    <>
      <PageTitle
        title="Batch & Class Students List - Admin Panel | Shree Mangalam School"
        description="View and manage the list of students assigned to specific batches and classes through the Admin Panel of Shree Mangalam School. Track academic allocation and attendance efficiently."
        url="/admin/batch"
        keywords="batch students list, class wise student list, admin student tracking, Shree Manglam student management, school batches and classes"
      />

      {loading ? (
        <CircularProgress sx={{ display: "block", margin: "auto" }} />
      ) : (
        <>
          <Typography
            variant="h5"
            align="center"
            gutterBottom
            sx={{
              pb: 3,
              fontFamily: "Poppins, sans-serif",
              color: "#2C3E50",
              letterSpacing: "2.5px",
              lineHeight: 1.8,
            }}
          >
            Batch & Class Students List
          </Typography>
          <Breadcrumbs
            aria-label="breadcrumb"
            sx={{
              mb: 5,
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            }}
          >
            <MUILink
              component={Link}
              to="/dashboard"
              sx={{ color: "inherit", textDecoration: "none" }}
            >
              Dashboard
            </MUILink>
            <MUILink
              component={Link}
              to="/admin/batch"
              sx={{ color: "inherit", textDecoration: "none" }}
            >
              Batch & Class
            </MUILink>
            <Typography sx={{ color: "primary.main" }}>Students</Typography>
          </Breadcrumbs>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="basic tabs example"
            >
              <Tab label="Students List" {...TabPanel(0)} />
              <Tab label="Add Student" {...TabPanel(1)} />
            </Tabs>
          </Box>

          <CustomTabPanel value={value} index={0} maxWidth>
            <Typography
              variant="h6"
              sx={{
                pb: 2,
                pt: 3,
                fontFamily: "Poppins, sans-serif",
                color: "#555",
                letterSpacing: "2.5px",
                lineHeight: 1.8,
              }}
            >
              Students Batch & Class List
            </Typography>

            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by Student Name, or Email"
              value={search}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <IconButton>
                    <SearchIcon sx={{ color: "#1976d2" }} />
                  </IconButton>
                ),
              }}
              sx={{
                mb: 3,
                bgcolor: "white",
                borderRadius: "20px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    border: "none",
                  },
                  "&:hover fieldset": {
                    border: "none",
                  },
                  "&.Mui-focused fieldset": {
                    border: "none",
                  },
                },
              }}
            />
            <Box
              style={{
                width: "auto",
                overflowY: "hidden",
                overflowX: "scroll",
              }}
            >
              <Box
                sx={{
                  pb: 2,
                  display: "flex",
                  width: "auto",
                  justifyContent: "right",
                }}
              ></Box>
              <Table
                key={filteredStudent.length}
                data={filteredStudent}
                columns={columns}
              />
            </Box>
          </CustomTabPanel>

          <CustomTabPanel value={value} index={1} maxWidth>
            <Typography
              variant="h6"
              sx={{
                pb: 2,
                pt: 3,
                fontFamily: "Poppins, sans-serif",
                color: "#555",
                letterSpacing: "2.5px",
                lineHeight: 1.8,
              }}
            >
              Add a new Students
            </Typography>

            <AddEnrollStudentBatch />
          </CustomTabPanel>
        </>
      )}
    </>
  );
};

export default EnrollStudentBatch;
